import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { GraduationCap, ShieldCheck, Filter as FilterIcon, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MENTOR_TYPE_LABEL = {
  DOCTORAL_GRADUATE: "Doctoral Graduate",
  FACULTY: "Faculty",
  DISSERTATION_COACH: "Dissertation Coach",
  RESEARCH_METHODOLOGY_MENTOR: "Research / Methodology Mentor",
};
const AVAILABILITY_LABEL = {
  AVAILABLE: "Available",
  LIMITED: "Limited",
  NOT_ACCEPTING: "Not currently accepting",
};
const AVAILABILITY_TONE = {
  AVAILABLE: "bg-emerald-50 text-emerald-800 border-emerald-200",
  LIMITED: "bg-amber-50 text-amber-800 border-amber-200",
  NOT_ACCEPTING: "bg-slate-100 text-slate-700 border-slate-200",
};

function MentorCard({ m, onOpen }) {
  return (
    <Card data-testid={`mentor-card-${m.id}`} className="p-5 hover:border-primary/40 transition-colors cursor-pointer" onClick={() => onOpen(m.id)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-lg font-black truncate">{m.display_name}</h3>
            {m.verification_status === "VERIFIED" && (
              <Badge data-testid="verified-badge" className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
                <ShieldCheck className="h-3 w-3 mr-1" /> Verified
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {MENTOR_TYPE_LABEL[m.mentor_type] || m.mentor_type} · {m.discipline || "—"}
          </p>
          {m.headline && <p className="mt-2 text-sm">{m.headline}</p>}
          {m.match_reasons && m.match_reasons.length > 0 && (
            <ul className="mt-3 space-y-0.5 text-xs text-muted-foreground">
              {m.match_reasons.slice(0, 3).map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <Sparkles className="h-3 w-3 mt-0.5 text-primary/60 shrink-0" /> {r}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap gap-1">
            {(m.expertise || []).slice(0, 4).map(e => (
              <span key={e} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                {e.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md border ${AVAILABILITY_TONE[m.availability] || ""}`}>
            {AVAILABILITY_LABEL[m.availability] || m.availability}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </Card>
  );
}

export default function Mentors() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [recommended, setRecommended] = useState([]);
  const [browse, setBrowse] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ discipline: "", methodology: "", expertise: "", supported_stage: "", availability: "" });
  const [q, setQ] = useState("");
  const [isMentor, setIsMentor] = useState(null);

  const loadRecommended = useCallback(async () => {
    try {
      const { data } = await api.get("/mentors/recommended?limit=6");
      setRecommended(data.results);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  const loadBrowse = useCallback(async (reset = true) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (q.trim()) params.set("q", q.trim());
      if (!reset && nextCursor) params.set("cursor", nextCursor);
      params.set("limit", "12");
      const { data } = await api.get(`/mentors?${params.toString()}`);
      setBrowse(reset ? data.results : [...browse, ...data.results]);
      setNextCursor(data.next_cursor);
      setHasMore(data.has_more);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [filters, q, nextCursor, browse]);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/mentors/me");
      setIsMentor(!!data.is_mentor);
    } catch { setIsMentor(false); }
  }, []);

  useEffect(() => { loadMe(); loadRecommended(); loadBrowse(true); }, []); // eslint-disable-line
  useEffect(() => { loadBrowse(true); }, [filters]); // eslint-disable-line

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8" data-testid="mentors-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <GraduationCap className="h-6 w-6 text-primary" />
            <h1 className="font-serif text-3xl font-black tracking-tight">Mentors</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Guidance from people who&rsquo;ve walked the path — graduates, faculty, and dissertation coaches
            who chose to make themselves available to doctoral candidates.
          </p>
        </div>
        <div className="flex gap-2">
          {isMentor === true ? (
            <Button variant="outline" data-testid="edit-mentor-profile-btn" onClick={() => nav("/mentors/me")}>
              Manage your mentor profile
            </Button>
          ) : isMentor === false ? (
            <Button data-testid="become-mentor-btn" onClick={() => nav("/mentors/me")}>
              Become a Mentor
            </Button>
          ) : null}
        </div>
      </header>

      {recommended.length > 0 && (
        <section data-testid="mentors-recommended">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Recommended for you</p>
          <div className="grid md:grid-cols-2 gap-4">
            {recommended.map(m => <MentorCard key={m.id} m={m} onOpen={(id) => nav(`/mentors/${id}`)} />)}
          </div>
        </section>
      )}

      <section data-testid="mentors-browse">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Browse mentors</p>
          <FilterIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <Card className="p-4 mb-4" data-testid="mentor-filters">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Input data-testid="filter-discipline" placeholder="Discipline" value={filters.discipline}
              onChange={e => setFilters({ ...filters, discipline: e.target.value })}
              onKeyDown={e => { if (e.key === "Enter") loadBrowse(true); }} className="h-9" />
            <select data-testid="filter-methodology" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.methodology} onChange={e => setFilters({ ...filters, methodology: e.target.value })}>
              <option value="">Any methodology</option>
              <option value="Qualitative">Qualitative</option>
              <option value="Quantitative">Quantitative</option>
              <option value="Mixed Methods">Mixed Methods</option>
            </select>
            <select data-testid="filter-expertise" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.expertise} onChange={e => setFilters({ ...filters, expertise: e.target.value })}>
              <option value="">Any expertise</option>
              <option value="irb">IRB</option>
              <option value="proposal_development">Proposal Development</option>
              <option value="defense_preparation">Defense Preparation</option>
              <option value="academic_writing">Academic Writing</option>
              <option value="statistical_analysis">Statistical Analysis</option>
              <option value="literature_review">Literature Review</option>
              <option value="data_analysis">Data Analysis</option>
              <option value="publishing">Publishing</option>
            </select>
            <select data-testid="filter-stage" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.supported_stage} onChange={e => setFilters({ ...filters, supported_stage: e.target.value })}>
              <option value="">Any stage</option>
              {["coursework","comps","topic_development","proposal","proposal_defense","irb","research","analysis","dissertation","final_review","defense","revisions","graduation","publishing"].map(s =>
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
            <select data-testid="filter-availability" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.availability} onChange={e => setFilters({ ...filters, availability: e.target.value })}>
              <option value="">Any availability</option>
              <option value="AVAILABLE">Available</option>
              <option value="LIMITED">Limited</option>
            </select>
            <Input data-testid="filter-q" placeholder="Search headline / bio" value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") loadBrowse(true); }} className="h-9" />
          </div>
        </Card>
        {browse.length === 0 ? (
          <Card className="p-10 text-center" data-testid="browse-empty">
            <GraduationCap className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">No mentors match your filters.</p>
            <p className="text-xs text-muted-foreground">Try broadening your criteria.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {browse.map(m => <MentorCard key={m.id} m={m} onOpen={(id) => nav(`/mentors/${id}`)} />)}
          </div>
        )}
        {hasMore && (
          <div className="mt-4 text-center">
            <Button variant="outline" data-testid="browse-load-more" onClick={() => loadBrowse(false)}>Load more</Button>
          </div>
        )}
      </section>
    </div>
  );
}
